import { StateCreator } from 'zustand';
import { createLens } from '@dhmk/zustand-lens';
import { nanoid } from 'nanoid';
import _ from 'lodash';
import { TERMINAL_NODE_ID } from '../../components/graph-editor/config';
import {
  type EdgeId,
  type EdgeInstance,
  type Graph,
  type GraphId,
  type GraphRef,
  type GraphValidation,
  type GraphEvaluation,
  type NodeId,
  type NodeInstance,
  NodeEvaluationStatus,
  EvaluationValueKind,
  NodeEvaluation,
  GraphRefType,
} from '../../components/graph-editor/types/graph';
import { createGraphAPI } from './graph';
import type { IGraph } from './graph';
import { createNodeAPI } from './node';
import type { INode } from './node';
import { createEdgeAPI } from './edge';
import type { IEdge } from './edge';
import { createEvaluationAPI } from './evaluation';
import type { IEvaluation } from './evaluation';
import { createValidationAPI } from './validation';
import type { IValidation } from './validation';
import { Result, ok, err } from 'neverthrow';
import { OptionType } from '@morph-mapper/types';
import type { GraphCommand } from '../../components/graph-editor/lib/commands';
import {
  GraphBatchQueryItem,
  GraphBatchRuntimeState,
} from '../../components/graph-editor/lib/batch/types';
import { getValues } from '@morph-mapper/utils';

export type { IGraph, INode, IEdge, IEvaluation, IValidation };

enum GraphErrorKind {
  NothingToDo = 'nothing_to_do',
  BATCH_ALREADY_CLAIMED = 'batch_already_claimed',
  BATCH_QUEUE_EMPTY = 'batch_queue_empty',
  BATCH_IN_FLIGHT = 'batch_in_flight',
  DOES_NOT_EXISTS = 'does_not_exists',
}

enum GraphDeleteOrigin {
  Explicit = 'explicit',
  Cascade = 'cascade',
}

export type GraphHistoryEntry = {
  id: string;
  timestamp: number;
  label?: string;
  command: GraphCommand;
  inverse?: GraphCommand;
};

export type GraphHistoryState = {
  past: GraphHistoryEntry[];
  future: GraphHistoryEntry[];
};

export type GraphError = {
  kind: GraphErrorKind;
  message: string;
};

// TODO: mapping to specific nodes for a graph.
export interface GraphsSlice {
  /*
   * Registry of all graphs.
   * Each graph stores the logical model and configurations used to drive evaluation and validation.
   */
  graphs: Record<GraphId, Graph>;
  /*
   * Registry of all graph evaluations.
   * Each evaluation stores the compiled templates and parsed results for each node in a graph.
   */
  evaluations: Record<GraphId, GraphEvaluation>;
  /*
   * Registry of all graph validations.
   * Each validation stores inferred types as well as any validation errors.
   */
  validations: Record<GraphId, GraphValidation>;
  /*
   * Registry for parent and child relations for all graphs. These records allow for quick traversal of the graph hierarchy,
   * used when working with nested graphs.
   */
  // Maps a graph ID to its parent graph IDs.
  graphParents: Record<GraphId, GraphId>;
  // Maps a graph ID to its child graph IDs.
  graphChildren: Record<GraphId, Set<GraphId>>;
  // Tracks soft-deleted graphs and deletion origin.
  graphTombstones: Record<GraphId, { by: GraphDeleteOrigin; at: number }>;
  // Per-graph command history, kept in-memory for the app session.
  graphHistories: Record<GraphId, GraphHistoryState>;

  /** WIP */
  batchQueue: GraphBatchQueryItem[];
  batchInFlight: GraphBatchQueryItem | null;
  batchStatus: GraphBatchRuntimeState | null;

  resetBatchSM: () => void;
  claimBatch: () => Result<GraphBatchQueryItem, GraphError>;
  finishBatch: (batchId?: string) => void;
  enqueueGraph: (graphId: GraphId, dirty?: NodeId[]) => void;

  ////
  setNodeTemplate: (graphId: GraphId, nodeId: NodeId, template: any) => void;
  getNodeTemplate: (graphId: GraphId, nodeId: NodeId) => Readonly<any>;

  setNodeEvaluation: (
    graphId: GraphId,
    nodeId: NodeId,
    evaluation: NodeEvaluation,
  ) => void;
  getNodeEvaluation: (
    graphId: GraphId,
    nodeId: NodeId,
  ) => Readonly<NodeEvaluation> | undefined;
  /** END WIP */

  /*
   * API factories which return functionality with a scoped context, this saves on repeated calls with unchanging ids.
   */
  graph: (id: GraphId) => IGraph;
  node: (graphId: GraphId, nodeId: NodeId) => INode;
  edge: (graphId: GraphId, edgeId: EdgeId) => IEdge;
  evaluation: (graphId: GraphId) => IEvaluation;
  validation: (graphId: GraphId) => IValidation;

  /*
   * Registers the relations of a new graph in the auxiliary data structures.
   * These are used to quickly fetch relations between graphs.
   */
  registerGraph: (parentGraphId: GraphId, newGraphId: GraphId) => void;

  getGraphParents: (graphId: GraphId) => GraphId;
  getGraphChildren: (graphId: GraphId) => GraphId[];

  // Operators on a single graph
  init: (params: {
    id?: GraphId;
    ref: GraphRef;
    nodes?: Record<NodeId, NodeInstance>;
    edges?: Record<EdgeId, EdgeInstance>;
    graphVersion?: number;
    topologyVersion?: number;
  }) => GraphId;
  deleteGraph: (id: GraphId) => void;
  restoreGraph: (id: GraphId) => void;
  copyTree: (sourceId: GraphId, ref: GraphRef) => GraphId;
  isGraphDeleted: (id: GraphId) => boolean;
  getHistory: (id: GraphId) => GraphHistoryState;
  setHistory: (id: GraphId, history: GraphHistoryState) => void;
  pushHistory: (id: GraphId, entry: GraphHistoryEntry) => void;
  undoHistory: (id: GraphId) => GraphHistoryEntry | null;
  redoHistory: (id: GraphId) => GraphHistoryEntry | null;
  clearHistory: (id: GraphId) => void;
  clearAllHistories: () => void;
  get: (id: GraphId) => Graph;
  getId: (id: GraphId) => GraphId;
  getRef: (id: GraphId) => GraphRef;
  setRef: (id: GraphId, ref: GraphRef) => void;
  getGraphVersion: (id: GraphId) => number;
  getTopologyVersion: (id: GraphId) => number;
  getGraphTemplate: (id: GraphId) => unknown;
  getGraphEvaluation: (id: GraphId) => Result<NodeEvaluation, GraphError>;
  bumpGraphVersion: (id: GraphId) => void;
  bumpTopologyVersion: (id: GraphId) => void;
  getNodeIds: (id: GraphId) => NodeId[];
  getNodes: (id: GraphId) => Record<NodeId, NodeInstance>;
  getEdges: (id: GraphId) => Record<EdgeId, EdgeInstance>;
  getEdge: (graphId: GraphId, edgeId: EdgeId) => EdgeInstance;
  setNodes: (id: GraphId, nodes: Record<NodeId, NodeInstance>) => void;
  setEdges: (id: GraphId, edges: Record<EdgeId, EdgeInstance>) => void;
  setGraph: (id: GraphId, graph: Graph) => void;
  getEvaluation: (graphId: GraphId) => GraphEvaluation;
  setEvaluation: (graphId: GraphId, evaluation: GraphEvaluation) => void;
  getValidation: (graphId: GraphId) => GraphValidation;
  setValidation: (graphId: GraphId, validation: GraphValidation) => void;
  delete: (id: GraphId) => void;

  // Operators on multiple graphs
  hasGraphs: () => boolean;
  setGraphs: (graphs: Record<GraphId, Graph>) => void;

  getNode: (graphId: GraphId, nodeId: NodeId) => NodeInstance;
}

export const createGraphsSlice: StateCreator<
  { graphs: GraphsSlice },
  [['zustand/immer', never]],
  [],
  GraphsSlice
> = (_set, _get) => {
  // Slices make use of a namespace, make current slice local namespace.
  const [set, get] = createLens(_set, _get, ['graphs']);

  const createDefaultGraph = (params: {
    id?: GraphId;
    ref: GraphRef;
    nodes?: Record<NodeId, NodeInstance>;
    edges?: Record<EdgeId, EdgeInstance>;
    graphVersion?: number;
    topologyVersion?: number;
  }): Graph => {
    const id = params.id ?? nanoid(10);

    return {
      id,
      ref: params.ref,
      graphVersion: params.graphVersion ?? 0,
      topologyVersion: params.topologyVersion ?? 0,
      nodes:
        params.nodes ??
        ({
          [TERMINAL_NODE_ID]: {
            id: TERMINAL_NODE_ID,
            kind: 'terminal',
            options: {},
          },
        } as Record<NodeId, NodeInstance>),
      edges: params.edges ?? {},
    };
  };

  const createDefaultEvaluation = (graph: Graph): GraphEvaluation => {
    return {
      graphId: graph.id,
      graphVersion: graph.graphVersion,
      inputContextHash: '',
      evaluatorVersion: 'graph-editor',
      evaluatedAt: 0,
      nodeTemplates: {},
      nodes: {
        [TERMINAL_NODE_ID]: {
          nodeId: TERMINAL_NODE_ID,
          status: NodeEvaluationStatus.Idle,
          valueRef: {
            kind: EvaluationValueKind.Empty,
          },
          error: null,
          fingerprint: 0,
        },
      },
    };
  };

  const createDefaultValidation = (graph: Graph): GraphValidation => {
    return {
      graphId: graph.id,
      graphVersion: graph.graphVersion,
      issues: [],
      nodes: {},
      validatedAt: 0,
    };
  };

  const getLiveGraph = (id: GraphId) => {
    const graph = get().graphs[id];
    if (!graph || get().isGraphDeleted(id)) {
      throw new Error('Graph does not exist');
    }
    return graph;
  };

  const collectGraphChildren = (graph: Graph): GraphId[] => {
    const children: GraphId[] = [];
    const nodes = graph.nodes ?? {};

    Object.values(nodes).forEach((node) => {
      const options = node.options ?? {};
      Object.values(options).forEach((option) => {
        if (option.type !== OptionType.Graph) return;
        if (typeof option.graphId !== 'string') return;
        children.push(option.graphId);
      });
    });

    return children;
  };

  const collectSubtree = (rootId: GraphId): GraphId[] => {
    const visited = new Set<GraphId>();
    const queue: GraphId[] = [rootId];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);

      const graph = get().graphs[current];
      if (!graph) continue;

      const relationChildren = Array.from(get().graphChildren[current] ?? []);
      const optionChildren = collectGraphChildren(graph);

      relationChildren.forEach((childId) => queue.push(childId));
      optionChildren.forEach((childId) => queue.push(childId));
    }

    return Array.from(visited);
  };

  const detachGraphRelations = (s: GraphsSlice, id: GraphId) => {
    const parentId = s.graphParents[id];
    if (parentId) {
      const children = s.graphChildren[parentId];
      if (children) {
        children.delete(id);
        if (children.size === 0) {
          delete s.graphChildren[parentId];
        }
      }
    }

    delete s.graphParents[id];
    delete s.graphChildren[id];
  };

  const createCopiedGraphRef = (
    sourceRef: GraphRef,
    parentGraphId: GraphId,
    fieldId: string,
  ): GraphRef => {
    if (sourceRef.kind === GraphRefType.Param) {
      return {
        kind: GraphRefType.Param,
        graphId: parentGraphId,
        fieldId,
        ownerNodeId: sourceRef.ownerNodeId,
        paramKey: sourceRef.paramKey,
      };
    }

    return {
      kind: GraphRefType.Field,
      fieldId,
    };
  };

  return {
    graphs: {},
    evaluations: {},
    validations: {},
    graphParents: {},
    graphChildren: {},
    graphTombstones: {},
    graphHistories: {},

    /** WIP */
    batchQueue: [],
    batchInFlight: null,
    batchStatus: null,

    resetBatchSM: () => {
      set((s) => {
        if (s.batchInFlight !== null) {
          s.batchQueue.push(s.batchInFlight);
        }

        s.batchInFlight = null;
        s.batchStatus = null;
      });
    },

    claimBatch: () => {
      if (get().batchInFlight !== null) {
        return err({
          kind: GraphErrorKind.BATCH_IN_FLIGHT,
          message: 'A batch query is already in flight',
        });
      }

      if (get().batchQueue.length === 0) {
        return err({
          kind: GraphErrorKind.BATCH_QUEUE_EMPTY,
          message: 'No batch queries in queue',
        });
      }

      const batch = get().batchQueue[0];
      set((s) => {
        s.batchInFlight = batch;
        s.batchQueue.shift();
      });

      return ok(batch);
    },
    finishBatch: (batchId?: string) => {
      set((s) => {
        if (!s.batchInFlight) {
          return;
        }
        if (batchId && s.batchInFlight.id !== batchId) {
          return;
        }
        s.batchInFlight = null;
        s.batchStatus = null;
      });
    },
    enqueueGraph: (graphId: GraphId, dirty?: NodeId[]) => {
      const graph = get().graphs[graphId];
      if (!graph || get().isGraphDeleted(graphId)) {
        return;
      }

      set((s) => {
        const queuedBatch = s.batchQueue.find(
          (batch) => batch.graphId === graphId,
        );
        if (queuedBatch) {
          dirty?.forEach((nodeId) => queuedBatch.dirty.add(nodeId));
          queuedBatch.graphVersion = graph.graphVersion;
          queuedBatch.topologyVersion = graph.topologyVersion;
          return;
        }

        s.batchQueue.push({
          id: nanoid(),
          graphId,
          entryId: graph.ref.fieldId,
          graphVersion: graph.graphVersion,
          topologyVersion: graph.topologyVersion,
          dirty: new Set(dirty ?? []),
        });
      });
    },
    /////
    setNodeTemplate: (graphId, nodeId, template) => {
      set((s) => {
        s.evaluations[graphId].nodeTemplates[nodeId] = template;
      });
    },
    getNodeTemplate: (graphId, id) => {
      return get().evaluations[graphId].nodeTemplates[id];
    },
    setNodeEvaluation: (graphId, nodeId, evaluation) => {
      set((s) => {
        s.evaluations[graphId].nodes[nodeId] = evaluation;
        s.evaluations[graphId].evaluatedAt = Date.now();
      });
    },
    getNodeEvaluation: (graphId, id) => {
      return get().evaluations[graphId].nodes[id];
    },
    /** END WIP */

    graph: (id) => createGraphAPI(set, get, id),
    node: (graphId, nodeId) => createNodeAPI(set, get, graphId, nodeId),
    edge: (graphId, edgeId) => createEdgeAPI(set, get, graphId, edgeId),
    evaluation: (graphId) => createEvaluationAPI(set, get, graphId),
    validation: (graphId) => createValidationAPI(set, get, graphId),

    registerGraph: (parentGraphId, newGraphId) => {
      set((s) => {
        // Register parent of new graph
        s.graphParents[newGraphId] = parentGraphId;

        // Register new graph in parent relations
        const children = s.graphChildren[parentGraphId] ?? new Set();
        children.add(newGraphId);
        s.graphChildren[parentGraphId] = children;
      });
    },

    getGraphParents: (graphId) => {
      return get().graphParents[graphId];
    },
    getGraphChildren: (graphId) => {
      return Array.from(get().graphChildren[graphId] ?? []);
    },

    init: (params) => {
      const graph = createDefaultGraph(params);

      set((s) => {
        s.graphs[graph.id] = graph;
        if (!s.evaluations[graph.id]) {
          s.evaluations[graph.id] = createDefaultEvaluation(graph);
        }
        if (!s.validations[graph.id]) {
          s.validations[graph.id] = createDefaultValidation(graph);
        }
      });

      return graph.id;
    },
    copyTree: (sourceId, ref) => {
      const copyRecursive = (
        currentSourceId: GraphId,
        currentRef: GraphRef,
      ): GraphId => {
        const sourceGraph = getLiveGraph(currentSourceId);
        const newGraphId = nanoid(10);

        const nodes = _.cloneDeep(sourceGraph.nodes);
        const edges = _.cloneDeep(sourceGraph.edges);
        const fieldId = currentRef.fieldId;

        // Scan all nodes and their options in the graph,
        // recursively copying any child graphs.
        getValues(nodes).forEach((node) => {
          getValues(node.options ?? {}).forEach((option) => {
            if (option.type !== OptionType.Graph) return;
            if (typeof option.graphId !== 'string') return;

            const childSourceGraph = getLiveGraph(option.graphId);
            option.graphId = copyRecursive(
              childSourceGraph.id,
              createCopiedGraphRef(childSourceGraph.ref, newGraphId, fieldId),
            );
          });
        });

        get().init({
          id: newGraphId,
          ref: currentRef,
          nodes,
          edges,
        });

        // Register new relations for the copied graph.
        if (currentRef.kind === GraphRefType.Param) {
          get().registerGraph(currentRef.graphId, newGraphId);
        }

        return newGraphId;
      };

      return copyRecursive(sourceId, ref);
    },
    deleteGraph: (id) => {
      const graph = get().graphs[id];
      if (!graph) {
        throw new Error('Graph does not exist');
      }
      if (get().isGraphDeleted(id)) {
        return;
      }

      const subtree = collectSubtree(id);
      const now = Date.now();

      set((s) => {
        subtree.forEach((graphId) => {
          if (!s.graphs[graphId]) return;

          const existing = s.graphTombstones[graphId];
          if (graphId === id) {
            s.graphTombstones[graphId] = {
              by: GraphDeleteOrigin.Explicit,
              at: now,
            };
          } else if (!existing) {
            s.graphTombstones[graphId] = {
              by: GraphDeleteOrigin.Cascade,
              at: now,
            };
          }

          if (!existing || graphId === id) {
            detachGraphRelations(s, graphId);
          }
        });
      });
    },
    restoreGraph: (id) => {
      const graph = get().graphs[id];
      if (!graph) {
        throw new Error('Graph does not exist');
      }
      if (!get().isGraphDeleted(id)) {
        return;
      }

      const subtree = collectSubtree(id);

      set((s) => {
        subtree.forEach((graphId) => {
          const target = s.graphs[graphId];
          if (!target) return;

          const tombstone = s.graphTombstones[graphId];
          if (graphId !== id && tombstone?.by === GraphDeleteOrigin.Explicit) {
            return;
          }

          delete s.graphTombstones[graphId];

          if (target.ref.kind === GraphRefType.Param) {
            s.graphParents[graphId] = target.ref.graphId;
            const children = s.graphChildren[target.ref.graphId] ?? new Set();
            children.add(graphId);
            s.graphChildren[target.ref.graphId] = children;
          }
        });
      });
    },
    isGraphDeleted: (id) => {
      return Boolean(get().graphTombstones[id]);
    },
    getHistory: (id) => {
      const existing = get().graphHistories[id];
      if (existing) {
        return {
          past: [...existing.past],
          future: [...existing.future],
        };
      }

      // Reading history must not mutate the store during selector evaluation.
      // Writers already initialise a missing slot when they need one.
      return {
        past: [],
        future: [],
      };
    },
    setHistory: (id, history) => {
      set((s) => {
        s.graphHistories[id] = {
          past: [...history.past],
          future: [...history.future],
        };
      });
    },
    pushHistory: (id, entry) => {
      set((s) => {
        const history = s.graphHistories[id] ?? { past: [], future: [] };
        s.graphHistories[id] = {
          past: [...history.past, entry],
          future: [],
        };
      });
    },
    undoHistory: (id) => {
      const history = get().graphHistories[id] ?? { past: [], future: [] };
      const last = history.past[history.past.length - 1];
      if (!last) return null;

      set((s) => {
        s.graphHistories[id] = {
          past: history.past.slice(0, -1),
          future: [last, ...history.future],
        };
      });

      return last;
    },
    redoHistory: (id) => {
      const history = get().graphHistories[id] ?? { past: [], future: [] };
      const next = history.future[0];
      if (!next) return null;

      set((s) => {
        s.graphHistories[id] = {
          past: [...history.past, next],
          future: history.future.slice(1),
        };
      });

      return next;
    },
    clearHistory: (id) => {
      set((s) => {
        s.graphHistories[id] = { past: [], future: [] };
      });
    },
    clearAllHistories: () => {
      set((s) => {
        s.graphHistories = {};
      });
    },
    get: (id) => {
      return getLiveGraph(id);
    },
    getId: (id) => {
      return getLiveGraph(id).id;
    },
    getRef: (id) => {
      return getLiveGraph(id).ref;
    },
    setRef: (id, ref) => {
      set((s) => {
        const graph = s.graphs[id];
        if (!graph || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        graph.ref = ref;
      });
    },
    getGraphVersion: (id) => {
      return getLiveGraph(id).graphVersion;
    },
    getTopologyVersion: (id) => {
      return getLiveGraph(id).topologyVersion;
    },
    getGraphTemplate: (id) => {
      const graph = get().graphs[id];
      if (!graph || get().isGraphDeleted(id)) {
        return null;
      }
      const evaluation = get().evaluations[id];
      if (!evaluation) {
        return null;
      }
      return evaluation.nodeTemplates[TERMINAL_NODE_ID];
    },
    getGraphEvaluation: (id) => {
      const graph = get().graphs[id];
      if (!graph || get().isGraphDeleted(id)) {
        return err({
          kind: GraphErrorKind.DOES_NOT_EXISTS,
          message: 'Graph does not exist',
        });
      }
      const evaluation = get().evaluations[id];
      if (!evaluation) {
        return err({
          kind: GraphErrorKind.DOES_NOT_EXISTS,
          message: 'Evaluation does not exist',
        });
      }
      const ev = evaluation.nodes[TERMINAL_NODE_ID];
      if (!ev) {
        return err({
          kind: GraphErrorKind.DOES_NOT_EXISTS,
          message: 'Evaluation does not exist',
        });
      }
      return ok(ev);
    },
    bumpGraphVersion: (id) => {
      set((s) => {
        const graph = s.graphs[id];
        if (!graph || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        graph.graphVersion += 1;
      });
    },
    bumpTopologyVersion: (id) => {
      set((s) => {
        const graph = s.graphs[id];
        if (!graph || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        graph.graphVersion += 1;
        graph.topologyVersion += 1;
      });
    },
    getNodeIds: (id) => {
      return Object.keys(getLiveGraph(id).nodes);
    },
    getNodes: (id) => {
      return getLiveGraph(id).nodes;
    },
    getEdges: (id) => {
      return getLiveGraph(id).edges;
    },
    getEdge: (graphId, edgeId) => {
      const graph = getLiveGraph(graphId);
      const edge = graph.edges[edgeId];
      if (!edge) {
        throw new Error('Edge does not exist');
      }
      return edge;
    },
    setNodes: (id, nodes) => {
      set((s) => {
        const graph = s.graphs[id];
        if (!graph || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        graph.nodes = nodes;
      });
    },
    setEdges: (id, edges) => {
      set((s) => {
        const graph = s.graphs[id];
        if (!graph || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        graph.edges = edges;
      });
    },
    setGraph: (id, graph) => {
      set((s) => {
        if (!s.graphs[id] || get().isGraphDeleted(id)) {
          throw new Error('Graph does not exist');
        }
        s.graphs[id] = graph;
      });
    },
    getEvaluation: (graphId) => {
      const graph = get().graphs[graphId];
      if (!graph || get().isGraphDeleted(graphId)) {
        throw new Error('Graph evaluation does not exist');
      }
      const evaluation = get().evaluations[graphId];
      if (!evaluation) {
        throw new Error('Graph evaluation does not exist');
      }
      return evaluation;
    },
    setEvaluation: (graphId, evaluation) => {
      set((s) => {
        const graph = s.graphs[graphId];
        if (!graph || get().isGraphDeleted(graphId)) {
          throw new Error('Graph evaluation does not exist');
        }
        s.evaluations[graphId] = evaluation;
      });
    },
    getValidation: (graphId) => {
      const graph = get().graphs[graphId];
      if (!graph || get().isGraphDeleted(graphId)) {
        throw new Error('Graph validation does not exist');
      }
      const validation = get().validations[graphId];
      if (!validation) {
        throw new Error('Graph validation does not exist');
      }
      return validation;
    },
    setValidation: (graphId, validation) => {
      set((s) => {
        const graph = s.graphs[graphId];
        if (!graph || get().isGraphDeleted(graphId)) {
          throw new Error('Graph validation does not exist');
        }
        s.validations[graphId] = validation;
      });
    },
    delete: (id) => {
      get().deleteGraph(id);
    },

    hasGraphs: () => {
      return Object.keys(get().graphs).length > 0;
    },
    // TODO: We probably want to have a specific hydrate() store function for each store,
    // this gives a unified API in dealing with different namespaces.
    setGraphs: (graphs) => {
      set((s) => {
        s.graphs = graphs;
        s.graphParents = {};
        s.graphChildren = {};

        // We do not store the evaluations and validations in the save file,
        // so we need to rebuild when hydrating.
        s.evaluations = Object.fromEntries(
          getValues(graphs).map((graph) => [
            graph.id,
            createDefaultEvaluation(graph),
          ]),
        );
        s.validations = Object.fromEntries(
          getValues(graphs).map((graph) => [
            graph.id,
            createDefaultValidation(graph),
          ]),
        );

        // Rebuild the relations registry.
        getValues(graphs).forEach((graph) => {
          if (graph.ref.kind !== GraphRefType.Param) return;

          s.graphParents[graph.id] = graph.ref.graphId;
          const children = s.graphChildren[graph.ref.graphId] ?? new Set();
          children.add(graph.id);
          s.graphChildren[graph.ref.graphId] = children;
        });

        s.graphTombstones = {};
        s.graphHistories = {};
        s.batchQueue = [];
        s.batchInFlight = null;
        s.batchStatus = null;
      });
    },
    getNode: (graphId, nodeId) => {
      const graph = get().graphs[graphId];
      if (!graph || get().isGraphDeleted(graphId)) {
        throw new Error('Graph does not exist');
      }

      const node = graph.nodes[nodeId];

      if (!node) {
        throw new Error('Node does not exist');
      }

      return node;
    },
  };
};
